import { useState, useEffect, FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { isWebAuthnSupported, isPlatformAuthenticatorAvailable } from '../services/webauthn';

enum AuthMethod {
  PASSWORD = 'password',
  PASSKEY = 'passkey',
}

function Signup() {
  const navigate = useNavigate();
  const { isAuthenticated, signupWithEmail, registerPasskey } = useAuth();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [passkeyAvailable, setPasskeyAvailable] = useState(false);
  const [passkeyRegistered, setPasskeyRegistered] = useState(false);
  const [authMethod, setAuthMethod] = useState<AuthMethod | null>(null);
  const [validationErrors, setValidationErrors] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    // Check if passkeys are supported
    if (isWebAuthnSupported()) {
      isPlatformAuthenticatorAvailable().then(setPasskeyAvailable);
    }
  }, []);

  // Validate name
  const validateName = (value: string) => {
    if (!value) {
      return 'Name is required';
    }
    if (value.length < 2) {
      return 'Name must be at least 2 characters';
    }
    return '';
  };

  // Validate email
  const validateEmail = (value: string) => {
    if (!value) {
      return 'Email is required';
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      return 'Please enter a valid email address';
    }
    return '';
  };

  // Validate password
  const validatePassword = (value: string) => {
    if (!value) {
      return 'Password is required';
    }
    if (value.length < 8) {
      return 'Password must be at least 8 characters';
    }

    // Check password strength: must contain at least 3 of 4 character types
    const hasLowerCase = /[a-z]/.test(value);
    const hasUpperCase = /[A-Z]/.test(value);
    const hasNumbers = /[0-9]/.test(value);
    const hasSpecialChars = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(value);

    const characterTypeCount = [hasLowerCase, hasUpperCase, hasNumbers, hasSpecialChars].filter(Boolean).length;

    if (characterTypeCount < 3) {
      return 'Password must contain at least 3 of: lowercase, uppercase, numbers, special characters';
    }

    return '';
  };

  // Validate confirm password
  const validateConfirmPassword = (value: string) => {
    if (!value) {
      return 'Please confirm your password';
    }
    if (value !== password) {
      return 'Passwords do not match';
    }
    return '';
  };

  // Check if basic info (name + email) is complete
  const isBasicInfoValid = () => {
    return (
      name &&
      email &&
      !validateName(name) &&
      !validateEmail(email)
    );
  };

  // Check if full form is valid (for password signup)
  const isFormValid = () => {
    return (
      isBasicInfoValid() &&
      password &&
      confirmPassword &&
      !validatePassword(password) &&
      !validateConfirmPassword(confirmPassword)
    );
  };

  // Handle name change with validation
  const handleNameChange = (value: string) => {
    setName(value);
    if (value) {
      setValidationErrors((prev) => ({ ...prev, name: validateName(value) }));
    } else {
      setValidationErrors((prev) => ({ ...prev, name: '' }));
    }
  };

  // Handle email change with validation
  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (value) {
      setValidationErrors((prev) => ({ ...prev, email: validateEmail(value) }));
    } else {
      setValidationErrors((prev) => ({ ...prev, email: '' }));
    }
  };

  // Handle password change with validation
  const handlePasswordChange = (value: string) => {
    setPassword(value);
    if (value) {
      setValidationErrors((prev) => ({ ...prev, password: validatePassword(value) }));
    } else {
      setValidationErrors((prev) => ({ ...prev, password: '' }));
    }
    // Re-validate confirm password if it's already filled
    if (confirmPassword) {
      setValidationErrors((prev) => ({
        ...prev,
        confirmPassword: value !== confirmPassword ? 'Passwords do not match' : '',
      }));
    }
  };

  // Handle confirm password change with validation
  const handleConfirmPasswordChange = (value: string) => {
    setConfirmPassword(value);
    if (value) {
      setValidationErrors((prev) => ({
        ...prev,
        confirmPassword: validateConfirmPassword(value),
      }));
    } else {
      setValidationErrors((prev) => ({ ...prev, confirmPassword: '' }));
    }
  };

  const handleEmailSignup = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    // Final validation check
    if (!isFormValid()) {
      setError('Please fix all validation errors before submitting');
      return;
    }

    setLoading(true);

    try {
      await signupWithEmail(email, name, password);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.message || 'Signup failed');
      setLoading(false);
    }
  };

  const handlePasswordChoice = () => {
    setError('');
    if (!isBasicInfoValid()) {
      setError('Please enter your name and email first');
      return;
    }
    setAuthMethod(AuthMethod.PASSWORD);
  };

  const handlePasskeyChoice = () => {
    setError('');
    if (!isBasicInfoValid()) {
      setError('Please enter your name and email first');
      return;
    }
    setAuthMethod(AuthMethod.PASSKEY);
  };

  const handlePasskeySignup = async () => {
    setError('');

    // Validate basic info is provided
    if (!isBasicInfoValid()) {
      setError('Please enter your name and email first');
      return;
    }

    setLoading(true);

    try {
      // Register the passkey (includes backend integration)
      await registerPasskey(email, name);
      setPasskeyRegistered(true);

      // Navigate to dashboard after successful registration
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.message || 'Passkey registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="signin-container">
      <div className="signin-form">
        <h2>Sign up for Cointrust.ai</h2>

        <form onSubmit={handleEmailSignup}>
          <label htmlFor="name">Name</label>
          <input
            id="name"
            type="text"
            placeholder="Enter your name"
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            required
            disabled={loading}
            style={{ borderColor: validationErrors.name ? '#dc3545' : undefined }}
          />
          {validationErrors.name && (
            <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>
              {validationErrors.name}
            </div>
          )}

          <label htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => handleEmailChange(e.target.value)}
            required
            disabled={loading}
            style={{ borderColor: validationErrors.email ? '#dc3545' : undefined }}
          />
          {validationErrors.email && (
            <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>
              {validationErrors.email}
            </div>
          )}

          {error && (
            <div style={{ color: 'red', fontSize: '14px', marginBottom: '10px' }}>
              {error}
              {(error.toLowerCase().includes('already') || error.toLowerCase().includes('registered')) && (
                <div style={{ marginTop: '8px' }}>
                  <Link to="/login" style={{ color: '#007bff', textDecoration: 'underline' }}>
                    Go to sign in page →
                  </Link>
                </div>
              )}
            </div>
          )}

          {passkeyRegistered && (
            <div style={{ color: 'green', fontSize: '14px', marginBottom: '10px' }}>
              ✓ Passkey registered successfully!
            </div>
          )}

          {/* Show auth method selector buttons if no method chosen yet */}
          {!authMethod && (
            <>
              <button
                type="button"
                className="continue-btn"
                onClick={handlePasswordChoice}
                disabled={loading || !isBasicInfoValid()}
              >
                Sign up with Password
              </button>

              <div className="divider">OR</div>

              {passkeyAvailable && (
                <button
                  type="button"
                  className="oauth-btn"
                  onClick={handlePasskeyChoice}
                  disabled={loading || !isBasicInfoValid()}
                >
                  Register Passkey
                </button>
              )}

              {!passkeyAvailable && (
                <button
                  type="button"
                  className="oauth-btn"
                  disabled
                  style={{ opacity: 0.5, cursor: 'not-allowed' }}
                >
                  Passkey Not Supported
                </button>
              )}
            </>
          )}

          {/* Show tab selector and fields when method is chosen */}
          {authMethod && (
            <div style={{
              animation: 'slideDown 0.3s ease-out',
              marginTop: '16px'
            }}>
              {/* Tab Selector */}
              <div style={{
                display: 'flex',
                borderBottom: '2px solid #e0e0e0',
                marginBottom: '20px'
              }}>
                <button
                  type="button"
                  onClick={() => setAuthMethod(AuthMethod.PASSWORD)}
                  style={{
                    flex: 1,
                    padding: '12px',
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    fontWeight: authMethod === AuthMethod.PASSWORD ? '600' : '400',
                    color: authMethod === AuthMethod.PASSWORD ? '#007bff' : '#6c757d',
                    borderBottom: authMethod === AuthMethod.PASSWORD ? '3px solid #007bff' : '3px solid transparent',
                    marginBottom: '-2px',
                    transition: 'all 0.2s ease'
                  }}
                >
                  Password
                </button>
                {passkeyAvailable && (
                  <button
                    type="button"
                    onClick={() => setAuthMethod(AuthMethod.PASSKEY)}
                    style={{
                      flex: 1,
                      padding: '12px',
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      fontWeight: authMethod === AuthMethod.PASSKEY ? '600' : '400',
                      color: authMethod === AuthMethod.PASSKEY ? '#007bff' : '#6c757d',
                      borderBottom: authMethod === AuthMethod.PASSKEY ? '3px solid #007bff' : '3px solid transparent',
                      marginBottom: '-2px',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    Passkey
                  </button>
                )}
              </div>

              {/* Password Fields */}
              {authMethod === AuthMethod.PASSWORD && (
                <>
                  <label htmlFor="password">Password</label>
                  <input
                    id="password"
                    type="password"
                    placeholder="Min 8 chars with 3 of: lowercase, uppercase, numbers, special chars"
                    value={password}
                    onChange={(e) => handlePasswordChange(e.target.value)}
                    required
                    disabled={loading}
                    style={{ borderColor: validationErrors.password ? '#dc3545' : undefined }}
                  />
                  {password && (
                    <div style={{ fontSize: '11px', marginTop: '4px', color: '#6c757d' }}>
                      <div>✓ At least 8 characters: {password.length >= 8 ? '✓' : '✗'}</div>
                      <div>✓ Contains 3 of 4 types (lowercase, uppercase, numbers, special): {
                        (() => {
                          const hasLower = /[a-z]/.test(password);
                          const hasUpper = /[A-Z]/.test(password);
                          const hasNum = /[0-9]/.test(password);
                          const hasSpecial = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
                          const count = [hasLower, hasUpper, hasNum, hasSpecial].filter(Boolean).length;
                          return count >= 3 ? '✓' : `${count}/3`;
                        })()
                      }</div>
                    </div>
                  )}
                  {validationErrors.password && (
                    <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>
                      {validationErrors.password}
                    </div>
                  )}

                  <label htmlFor="confirmPassword">Confirm Password</label>
                  <input
                    id="confirmPassword"
                    type="password"
                    placeholder="Confirm your password"
                    value={confirmPassword}
                    onChange={(e) => handleConfirmPasswordChange(e.target.value)}
                    required
                    disabled={loading}
                    style={{ borderColor: validationErrors.confirmPassword ? '#dc3545' : undefined }}
                  />
                  {validationErrors.confirmPassword && (
                    <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>
                      {validationErrors.confirmPassword}
                    </div>
                  )}

                  <button type="submit" className="continue-btn" disabled={loading || !isFormValid()} style={{ marginTop: '16px' }}>
                    {loading ? 'Signing up...' : 'Sign up'}
                  </button>
                </>
              )}

              {/* Passkey Registration */}
              {authMethod === AuthMethod.PASSKEY && (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  <p style={{ marginBottom: '20px', color: '#6c757d' }}>
                    Use your device's biometric authentication (Face ID, Touch ID, or Windows Hello) to create a passkey.
                  </p>
                  <button
                    type="button"
                    className="continue-btn"
                    onClick={handlePasskeySignup}
                    disabled={loading || passkeyRegistered}
                  >
                    {loading ? 'Registering...' : passkeyRegistered ? '✓ Passkey Registered' : 'Register Passkey'}
                  </button>
                </div>
              )}
            </div>
          )}
        </form>

        <div className="privacy-note">
          By signing up, you agree to our <a href="#">Terms of Service</a> and <a href="#">Privacy Policy</a>.
        </div>

        <div className="login-link">
          Already have an account? <Link to="/login">Sign in</Link>
        </div>
      </div>
    </div>
  );
}

export default Signup;
